import { ReactNode } from "react";

import { Divider, Radio } from "~/ui";

/**
 * The shell both print-time source selectors render (SC-3763).
 *
 * "Print with: built-in / custom template" is one choice asked in two places — of a document in
 * each order-document modal, and of a label in the Generate Label form. What differs is which
 * slot is being asked about and which query answers it; what must NOT differ is the chrome, the
 * built-in option, the disabled-with-reason treatment or the heading. Those live here.
 *
 * EXTRACTED RATHER THAN GENERALISED. The obvious alternative was to widen `PrintSourceRadio`
 * with a kind and let it pick its own query, the way `activateBlockedReason` was widened in
 * SC-3939. It does not transfer: that radio also carries SC-3869's base-adoption option, which
 * is document-only — adoption offers no labels, and `setDefaultOutputTemplate` explicitly
 * refuses `requireUnassigned` for them. Widening would have carried a dead limb into a surface
 * that cannot use it.
 *
 * THE WRAPPER OWNS ITS OWN VALUE SPACE, which is why this takes a string rather than a boolean.
 * A label chooses between two things; a document may choose between three, because ShelfCycle's
 * standard is a distinct option that the form has to sequence an adoption for. Forcing both
 * through a boolean would push that third state back into a side-channel, which is what the
 * document radio already does with `baseChosen` and the reason it needs a `useState` at all.
 *
 * THE CALLER DECIDES WHETHER TO RENDER AT ALL. This does not read the beta flag; each wrapper
 * does, and returns null when it is off, so a form outside the beta is byte-identical to what it
 * was. Reading the flag here would mean this file deciding when its own absence is correct.
 */

export interface PrintSourceChoiceProps {
  /** The option currently chosen. "legacy" is the built-in; the rest are the wrapper's own. */
  selected: string;
  onSelect: (next: string) => void;
  /** What the built-in option is called — "Built-in document", "Built-in label". */
  legacyLabel: string;
  /** The sentence under the group, which differs by what the surface offers below it. */
  description: string;
  /** The non-legacy option(s): a plain custom-template radio, or the document's base variant. */
  children: ReactNode;
}

export const PrintSourceChoice = ({ selected, onSelect, legacyLabel, description, children }: PrintSourceChoiceProps): JSX.Element => (
  <>
    <Divider my="md" />
    <Radio.Group label="Print with" description={description} value={selected} onChange={onSelect}>
      <Radio value="legacy" label={legacyLabel} />
      {children}
    </Radio.Group>
  </>
);
